import { DateTime } from 'luxon'
import { createPinia, defineStore, setActivePinia } from 'pinia'

setActivePinia(createPinia())

export const useMBTV = defineStore('mbtv', () => {
  const token = ref<string | null>(window.localStorage.getItem('mbtv_token'))
  const mbtvId = ref<number | null>(Number.parseInt(window.localStorage.getItem('mbtv_id') ?? '0'))
  const isLoading = ref<boolean>(true)

  const isAuthenticated = ref<boolean>(false)

  const config = useRuntimeConfig()

  // markRaw: private class fields (#host) are unreadable through a reactive proxy
  const mbtv = markRaw(
    new MBTV({
      client_id: config.public.mbtvClient,
      host: config.public.mbtvUrl,
      redirect_url: `${window.origin}/`,
    }),
  )

  mbtv
    .init({
      token: token.value ?? undefined,
      mbtvId: mbtvId.value ?? undefined,
    })
    .then((auth) => {
      if (auth) {
        setTokens()
        isAuthenticated.value = true
      }
      isLoading.value = false
    })
    .catch((_) => {
      isLoading.value = false
      clearTokens()
    })

  function setTokens() {
    window.localStorage.setItem('mbtv_token', mbtv.token ?? '')
    window.localStorage.setItem('mbtv_id', (mbtv.mbtv_id ?? 0).toString())
  }

  function clearTokens() {
    window.localStorage.removeItem('mbtv_token')
    window.localStorage.removeItem('mbtv_id')
    token.value = null
    isAuthenticated.value = false
  }

  async function login() {
    mbtv.login()
  }

  async function checkValidation() {
    const me = await mbtv.me()

    if (!me?.birthday) return false
    const birthday = DateTime.fromISO(me?.birthday)

    if (-birthday.diffNow('years').years < 18) return false

    return me.optigemVerificationState.toLowerCase() === 'verifiziert'
  }

  async function logout() {
    if (!isAuthenticated.value) return
    await mbtv.logout()
    clearTokens()
    isAuthenticated.value = false
    window.localStorage.removeItem('shown_expiring_list')
  }

  async function waitForMBTV() {
    await until(isLoading).toBe(false, { timeout: 1000 })
  }

  async function updateToken() {
    if (isAuthenticated.value)
      try {
        const refresh = await mbtv.refreshToken()
        if (refresh) {
          setTokens()
        }
        else {
          clearTokens()
        }
      }
      catch (_) {
        clearTokens()
      }
  }

  return {
    login,
    logout,
    isAuthenticated,
    waitForMBTV,
    updateToken,
    mbtv,
    checkValidation,
  }
})
